'use client';
import { digitalSaleState } from '@/lib/commerce/preorder-policy';

import { useId, useState } from 'react';
import { UserRound } from 'lucide-react';
import { bookIntroduction, bookPurchasePresentation, publicWebsiteHref } from '@/lib/book-detail-presentation';
import detailStyles from './book-detail.module.css';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { SaveAccountItemButton } from '@/components/account/save-account-item-button';
import { useCart } from '@/components/cart/cart-context';
import { PageIntro } from '@/components/platform/page-intro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useHomepageSettings } from '@/hooks/use-homepage-settings';
import { usePublicTitle, usePublicTitles } from '@/hooks/use-public-titles';
import { getUploadedImage, type ResolvedImage } from '@/lib/book-image';
import { ROUTES } from '@/lib/routes';
import type { DigitalBookFormat } from '@/lib/platform/storefront-checkout';
import type { TitleDoc } from '@/types/platform';

function NeutralBookCover({ title }: { title: string }) {
 return (
 <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(239,207,185,.55),transparent_38%),linear-gradient(135deg,#fff8ef,#ead8c6)] p-8 text-center">
 <p className="font-headline text-3xl leading-tight text-charcoal">{title}</p>
 </div>
 );
}

function CoverImage({
 cover,
 title,
 width,
 height,
 priority = false,
 detail = false,
}: {
 cover: ResolvedImage | null;
 title: string;
 width: number;
 height: number;
 priority?: boolean;
 detail?: boolean;
}) {
 const [failedSrc, setFailedSrc] = useState<string | null>(null);

 if (!cover || cover.src === failedSrc) return <div className={detail ? 'mx-auto aspect-[2/3] max-h-[70svh] w-full max-w-[320px]' : 'h-full'}><NeutralBookCover title={title} /></div>;

 return (
 <Image
 src={cover.src}
 alt={cover.alt}
 width={width}
 height={height}
 className="h-full w-full object-cover"
 priority={priority}
 onError={() => setFailedSrc(cover.src)}
 />
 );
}

function resolveTitleCover(title: TitleDoc, defaultCover?: ResolvedImage | null) {
 const coverUrl = title.storefrontCoverUrl || title.coverUrl;
 if (coverUrl) {
 return { src: coverUrl, alt: title.storefrontCoverAlt || title.coverAlt || title.title };
 }
 if (defaultCover?.src) {
 return { src: defaultCover.src, alt: title.coverAlt || defaultCover.alt };
 }
 return null;
}

function AuthorAvatar({ src, name }: { src: string; name: string }) {
 const [failed, setFailed] = useState<string | null>(null);
 return <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-blush/70 bg-cream">
 {failed === src ? <UserRound aria-label={`${name} photo unavailable`} role="img" className="h-full w-full p-5 text-charcoal/60" /> : <Image src={src} alt={name} fill sizes="64px" className="object-cover" onError={() => setFailed(src)} />}
 </div>;
}

function hasLoggedInPreviewAccess(title: TitleDoc) {
 return Boolean(title.samplePdfAssetId || title.aboutPreviewUrl || title.firstChapterPreviewUrl || title.previewFileUrl || title.sampleChapterUrl);
}

function buildPreviewHref(titleId: string) {
 return `/api/titles/${encodeURIComponent(titleId)}/preview`;
}

function formatMoney(value: number, currency: 'EUR' | 'USD') {
 return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

function stableCartId(title: TitleDoc) {
 const source = title.titleId || title.slug || title.title;
 let hash = 0;
 for (let index = 0; index < source.length; index += 1) {
 hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
 }
 return Math.abs(hash);
}

function TitleCard({ title }: { title: TitleDoc }) {
 const cover = resolveTitleCover(title);
 const hasPreview = hasLoggedInPreviewAccess(title);

 return (
 <Card className="overflow-hidden rounded-[2rem] border border-blush/70 bg-white shadow-sm">
 <div className="aspect-[4/5] overflow-hidden bg-white">
 <CoverImage cover={cover} title={title.title} width={700} height={900} />
 </div>
 <CardHeader>
 <div className="flex flex-wrap gap-2">
 <Badge variant="secondary" className="bg-cream text-charcoal/80">{title.category || 'General'}</Badge>
 {title.featured ? <Badge variant="secondary" className="bg-[#5F8769] text-white">Featured</Badge> : null}
 {hasPreview ? <Badge variant="secondary" className="bg-white text-charcoal/80">Preview</Badge> : null}
 </div>
 <CardTitle className="font-headline text-2xl text-charcoal">{title.title}</CardTitle>
 <CardDescription className="text-charcoal/70">{title.marketingHook || title.shortDescription || title.subtitle || 'A book from The Untangled Life and Lumina Press.'}</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="flex items-center justify-between text-sm text-charcoal/75">
 <span>{title.availableDigitalFormats?.length ? title.availableDigitalFormats.map((format) => format.toUpperCase()).join(' or ') : 'Digital edition pending'}</span>
 <span className="font-semibold text-charcoal">{formatMoney(Number(title.price || 0), title.currency || 'EUR')}</span>
 </div>
 {title.authorDisplayName ? <p className="text-sm text-charcoal/70">By {title.authorDisplayName}</p> : null}
 <div className="flex flex-wrap gap-3">
 <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]">
 <Link href={`/titles/${title.slug}`}>Open title</Link>
 </Button>
 </div>
 </CardContent>
 </Card>
 );
}

export function PublicTitlesPanel({ initialTitles = [] }: { initialTitles?: TitleDoc[] }) {
 const { titles, loading, error } = usePublicTitles(initialTitles);

 return (
 <section className="bg-cream py-20">
 <div className="mx-auto max-w-6xl px-4">
 <PageIntro eyebrow="Books" title="Books for the next step" description="Browse books for clarity, boundaries, recovery, and a steadier sense of self.">
 <div className="mt-4 flex flex-wrap gap-3">
 <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]"><Link href={ROUTES.shop}>Browse main shop</Link></Button>
 <Button asChild variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60"><Link href={ROUTES.luminaPress}>Discover Lumina Press</Link></Button>
 </div>
 </PageIntro>

 {loading ? <p className="mt-8 text-sm text-charcoal/70">Loading published titles...</p> : error ? <p className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : titles.length ? (
 <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
 {titles.map((title) => <TitleCard key={title.titleId} title={title} />)}
 </div>
 ) : (
 <Card className="mt-8 rounded-[2rem] border border-blush/70 bg-white shadow-sm">
 <CardContent className="p-6 text-sm text-charcoal/75">No published titles yet. Promote a Lumina project from Studio {'->'} Publish to create the first storefront title.</CardContent>
 </Card>
 )}
 </div>
 </section>
 );
}

export function PublicTitleDetailPanel({
 titleSlug,
 initialTitle,
 checkoutAvailable,
 previewMode = false,
}: {
 titleSlug: string;
 initialTitle: TitleDoc;
 checkoutAvailable: boolean;
 previewMode?: boolean;
}) {
 const { title, loading, error } = usePublicTitle(titleSlug, initialTitle, previewMode);
 const router = useRouter();
 const { settings } = useHomepageSettings();
 const { dispatch, hydrated } = useCart();
 const [selectedFormat, setSelectedFormat] = useState<DigitalBookFormat | null>(null);
 const [formatError, setFormatError] = useState<string | null>(null);
 const defaultCover = previewMode ? null : getUploadedImage(settings?.defaultTitleCoverUrl, settings?.defaultTitleCoverAlt, 'Default book cover');
 const cover = title ? resolveTitleCover(title, defaultCover) : null;
 const intro = title ? bookIntroduction(title) : null;
 const purchase = title ? bookPurchasePresentation(title, checkoutAvailable) : null;
 const authorWebsite = publicWebsiteHref(title?.authorWebsiteUrl);
 const formatId = useId();
 const hasPreview = Boolean(title?.samplePdfAssetId);
 const availableFormats = purchase?.formats ?? [];
 const canPurchase = Boolean(!previewMode && selectedFormat && availableFormats.includes(selectedFormat) && !purchase?.blocked);
 const hardcoverStatus = title?.hardcoverStatus || (title?.amazonHardcoverUrl ? 'available' : 'hidden');
 const showHardcover = hardcoverStatus !== 'hidden';
 const visibleReviews = (title?.reviews ?? []).filter((review) => review.status !== 'hidden' && review.body?.trim());
 const hasBookInformation = Boolean(title?.description?.trim() || intro?.shortDescription || intro?.audience || intro?.bullets.length || title?.previewExcerpt?.trim() || title?.praiseQuote?.trim() || title?.keywords?.some(keyword => keyword.trim()));
 const hasAuthor = Boolean(title?.authorDisplayName?.trim() || title?.authorBio?.trim() || title?.authorAvatarUrl || authorWebsite);

 function handleAddToCart() {
 if (previewMode) return false;
 if (!title || !selectedFormat || !availableFormats.includes(selectedFormat)) {
 setFormatError('Choose EPUB or PDF before adding this book to your cart.');
 return false;
 }
 if (!canPurchase) return false;
 setFormatError(null);
 dispatch({
 type: 'ADD',
 item: {
 id: stableCartId(title),
 itemType: 'title',
 slug: title.slug,
 title: title.title,
 price: Number(title.price || 0),
 quantity: 1,
 currency: title.currency || 'EUR',
 coverUrl: title.storefrontCoverUrl || title.coverUrl || cover?.src,
 routeHref: `${ROUTES.titles}/${title.slug}`,
 digitalFormat: selectedFormat,
 digitalSaleState: digitalSaleState(title),
 availableDigitalFormats: availableFormats,
 },
 });
 return true;
 }

 function handleBuyNow() {
 if (handleAddToCart()) router.push(ROUTES.checkout);
 }

 return (
 <section ref={node => { if (node) node.inert = previewMode; }} data-book-detail className={`${detailStyles.detail} bg-cream py-10 sm:py-16`}>
 <div className="mx-auto max-w-6xl px-4">
 {loading ? <p className="text-sm text-charcoal/70">Loading title...</p> : error ? <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : title ? (
 <div className="space-y-10">
 <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,350px)_minmax(0,1fr)]">
 <Card data-book-cover className="mx-auto w-full max-w-[390px] self-start rounded-[2rem] border border-blush/70 bg-white p-3 shadow-sm">
 <CoverImage cover={cover} title={title.title} width={900} height={1100} priority detail />
 </Card>

 <div data-book-purchase className="min-w-0 space-y-5">
 <div className="flex flex-wrap gap-2">
 <Badge variant="secondary" className="bg-cream text-charcoal/80">{title.category || 'General'}</Badge>
 {title.featured ? <Badge variant="secondary" className="bg-[#52775D] text-white">Featured</Badge> : null}
 <Badge variant="secondary" className="bg-cream text-charcoal/80">{purchase?.badge}</Badge>
 {showHardcover ? <Badge variant="secondary" className="bg-cream text-charcoal/80">{hardcoverStatus === 'coming-soon' ? 'Hardcover coming soon' : 'Hardcover available'}</Badge> : null}
 </div>
 <div>
 <h1 className="font-headline text-4xl text-charcoal sm:text-5xl">{title.title}</h1>
 {intro?.subtitle ? <p className="mt-3 whitespace-pre-line text-xl text-charcoal/80">{intro.subtitle}</p> : null}
 {intro?.hook ? <p className="mt-4 max-w-3xl whitespace-pre-line text-base leading-7 text-charcoal/80">{intro.hook}</p> : null}
 </div>
 <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-charcoal/80">
 <span data-book-price className="text-2xl font-semibold text-charcoal">{formatMoney(Number(title.price || 0), title.currency || 'EUR')}</span>
 {title.pageCount ? <span>{title.pageCount} pages</span> : null}
 {Number(title.salesCount || 0) > 0 ? <span>{Number(title.salesCount || 0).toLocaleString()} sold</span> : null}
 {Number(title.averageRating || 0) > 0 ? <span>{Number(title.averageRating || 0).toFixed(1)} stars ({Number(title.reviewCount || visibleReviews.length)} reviews)</span> : null}
 </div>
 {availableFormats.length ? <div className="space-y-2">
 <label htmlFor={`${formatId}-format`} className="block text-sm font-semibold text-charcoal">Digital version</label>
 <select id={`${formatId}-format`} disabled={!hydrated} value={selectedFormat ?? ''} onChange={(event) => { setSelectedFormat(event.target.value as DigitalBookFormat); setFormatError(null); }} aria-describedby={`${formatId}-format-help`} className="min-h-11 w-full rounded-xl border border-blush bg-white px-3 py-3 text-base text-charcoal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra">
 <option value="" disabled>Choose a digital version</option>
 {availableFormats.map(format => <option key={format} value={format}>{format.toUpperCase()}</option>)}
 </select>
 <p id={`${formatId}-format-help`} className="text-xs leading-6 text-charcoal/80">{selectedFormat === 'epub' ? 'EPUB: best for e-readers and adjustable text.' : selectedFormat === 'pdf' ? 'PDF: fixed-layout file for reading or printing.' : 'Choose your preferred reading format.'}</p>
 {formatError ? <p role="alert" className="text-sm text-red-700">{formatError}</p> : null}
 </div> : null}
 <div className="flex flex-wrap gap-3">
 <Button type="button" disabled={!canPurchase} aria-describedby={`${formatId}-delivery`} className="bg-[#52775D] text-white hover:bg-[#46664F] disabled:opacity-100 disabled:bg-[#e1e7e2] disabled:text-[#39493d]" onClick={handleAddToCart}>
 Add to cart
 </Button>
 <Button type="button" disabled={!canPurchase} aria-describedby={`${formatId}-delivery`} variant="outline" className="border-terra bg-white text-charcoal hover:bg-cream disabled:opacity-100 disabled:text-charcoal/70" onClick={handleBuyNow}>{purchase?.state === 'preorder' ? 'Pre-order now' : 'Buy now'}</Button>
 </div>
 {!previewMode ? <SaveAccountItemButton
 itemType="title"
 itemId={title.titleId || title.slug}
 title={title.title}
 href={`${ROUTES.titles}/${title.slug}`}
 description={title.shortDescription || title.subtitle || title.marketingHook || title.description}
 imageUrl={title.storefrontCoverUrl || title.coverUrl}
 imageAlt={title.storefrontCoverAlt || title.coverAlt || title.title}
 className="max-w-full whitespace-normal border-blush bg-white text-charcoal hover:bg-blush/60"
 /> : null}
 <div className="space-y-3 border-t border-blush/70 pt-4">
 <p id={`${formatId}-delivery`} className="text-sm leading-6 text-charcoal/80">{purchase?.deliveryNotice}</p>
 <p className="text-xs leading-6 text-charcoal/75">Your selected format is recorded on the order. By continuing, you agree to the <Link className="underline underline-offset-4" href={ROUTES.terms}>Terms</Link> and acknowledge the <Link className="underline underline-offset-4" href={ROUTES.refundPolicy}>digital refund policy</Link>. For accessibility help, <Link className="underline underline-offset-4" href={ROUTES.contact}>contact support</Link>.</p>
 </div>
 {hasPreview && !previewMode ? <div>
 <Button asChild variant="outline" className="max-w-full whitespace-normal border-blush bg-white text-charcoal hover:bg-blush/60"><a href={buildPreviewHref(title.titleId)} target="_blank" rel="noreferrer">Open free three-chapter PDF sample</a></Button>
 <p className="mt-2 text-xs leading-6 text-charcoal/75">Read the public PDF sample. No account is required.</p>
 </div> : null}
 {showHardcover ? <div className="text-sm leading-6 text-charcoal/80">
 <p className="font-semibold text-charcoal">Hardcover edition</p>
 {hardcoverStatus === 'available' && title.amazonHardcoverUrl ? (
 <a href={title.amazonHardcoverUrl} target="_blank" rel="noreferrer" className="inline-block py-2 underline underline-offset-4">{title.hardcoverLabel || 'Buy hardcover on Amazon'}</a>
 ) : (
 <p className="mt-1">{title.hardcoverLabel || (hardcoverStatus === 'available' ? 'Hardcover purchase link is currently unavailable.' : 'Hardcover edition coming soon.')}</p>
 )}
 </div> : null}
 <nav aria-label="Book navigation" className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-charcoal/80">
 <Link className="py-2 underline underline-offset-4" href={ROUTES.shop}>Back to shop</Link>
 <Link className="py-2 underline underline-offset-4" href={ROUTES.cart}>View cart</Link>
 </nav>
 </div>
 </div>

 {(hasBookInformation || hasAuthor || visibleReviews.length || title.backCoverUrl) ? <div data-book-information className="space-y-8 rounded-[2rem] border border-blush/70 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
 {hasBookInformation ? <section aria-labelledby={`${formatId}-about`} className="space-y-6">
 <h2 id={`${formatId}-about`} className="font-headline text-3xl font-semibold text-charcoal sm:text-4xl">About this book</h2>
 <div className="max-w-[70ch] space-y-5 text-base leading-8 text-charcoal/80">
 {intro?.shortDescription ? <p className="whitespace-pre-line">{intro.shortDescription}</p> : null}
 {title.description?.trim() ? <p className="whitespace-pre-line">{title.description}</p> : null}
 {intro?.audience ? <p className="whitespace-pre-line"><span className="font-semibold text-charcoal">For readers: </span>{intro.audience}</p> : null}
 </div>
 {intro?.bullets.length ? <section className="max-w-[70ch] border-l-2 border-blush pl-5" aria-labelledby={`${formatId}-benefits`}>
 <h3 id={`${formatId}-benefits`} className="mb-3 font-headline text-xl font-semibold text-charcoal">What you’ll find inside</h3>
 <ul className="list-disc space-y-2 pl-5 text-base leading-7 text-charcoal/80">
 {intro.bullets.map(bullet => <li key={bullet} className="whitespace-pre-line">{bullet}</li>)}
 </ul>
 </section> : null}
 {(title.previewExcerpt?.trim() || title.praiseQuote?.trim()) ? <div className="grid gap-6 border-t border-blush/70 pt-6 md:grid-cols-2">
 {title.previewExcerpt?.trim() ? <section className="min-w-0" aria-labelledby={`${formatId}-excerpt`}>
 <h3 id={`${formatId}-excerpt`} className="mb-3 font-headline text-xl font-semibold text-charcoal">Preview excerpt</h3>
 <p className="max-w-[70ch] whitespace-pre-line text-base leading-8 text-charcoal/80">{title.previewExcerpt}</p>
 </section> : null}
 {title.praiseQuote?.trim() ? <figure className="min-w-0 border-l-2 border-blush pl-5">
 <blockquote className="max-w-[70ch] whitespace-pre-line font-headline text-xl italic leading-8 text-charcoal">“{title.praiseQuote}”</blockquote>
 {title.praiseSource?.trim() ? <figcaption className="mt-3 text-sm text-charcoal/75">— {title.praiseSource}</figcaption> : null}
 </figure> : null}
 </div> : null}
 {title.keywords?.some(keyword => keyword.trim()) ? <p className="max-w-[70ch] text-sm leading-7 text-charcoal/75"><span className="font-semibold">Topics: </span>{title.keywords.filter(keyword => keyword.trim()).join(' · ')}</p> : null}
 </section> : null}

 {hasAuthor ? <section aria-labelledby={`${formatId}-author`} className="border-t border-blush/70 pt-6">
 <h2 id={`${formatId}-author`} className="mb-4 font-headline text-2xl font-semibold text-charcoal">About the author</h2>
 <div className="flex max-w-[75ch] flex-col items-start gap-4 min-[480px]:flex-row">
 {title.authorAvatarUrl ? <AuthorAvatar src={title.authorAvatarUrl} name={title.authorDisplayName || 'Author'} /> : null}
 <div className="min-w-0 space-y-2">
 {title.authorDisplayName?.trim() ? <p className="text-base font-semibold text-charcoal">{title.authorDisplayName}</p> : null}
 {title.authorBio?.trim() ? <p className="whitespace-pre-line text-sm leading-7 text-charcoal/80">{title.authorBio}</p> : null}
 {authorWebsite ? <a href={authorWebsite} target="_blank" rel="noreferrer" className="inline-block py-2 text-sm text-charcoal underline underline-offset-4">Visit author website<span className="sr-only"> (opens in a new tab)</span></a> : null}
 </div>
 </div>
 </section> : null}

 {visibleReviews.length ? <section aria-labelledby={`${formatId}-reviews`} className="border-t border-blush/70 pt-6">
 <h2 id={`${formatId}-reviews`} className="mb-4 font-headline text-2xl font-semibold text-charcoal">Reader reviews</h2>
 <div className="grid gap-6 md:grid-cols-2">
 {visibleReviews.slice(0, 6).map(review => <article key={review.reviewId || `${review.authorName}-${review.body}`} className="min-w-0 space-y-3">
 <p className="text-sm font-semibold text-charcoal">{Number(review.rating || 5).toFixed(1)} / 5 stars</p>
 <p className="whitespace-pre-line text-sm leading-7 text-charcoal/80">{review.body}</p>
 <p className="text-xs text-charcoal/75">{review.authorName}{review.source ? ` — ${review.source}` : ''}</p>
 </article>)}
 </div>
 </section> : null}

 {title.backCoverUrl ? <section aria-labelledby={`${formatId}-covers`} className="border-t border-blush/70 pt-6">
 <h2 id={`${formatId}-covers`} className="mb-4 font-headline text-2xl font-semibold text-charcoal">Front and back covers</h2>
 <div className="grid max-w-3xl items-start gap-6 sm:grid-cols-2">
 <div data-book-cover className="min-w-0 rounded-2xl border border-blush/70 bg-cream p-3"><CoverImage cover={cover} title={title.title} width={700} height={900} detail /></div>
 <div data-book-cover className="min-w-0 rounded-2xl border border-blush/70 bg-cream p-3"><CoverImage cover={{ src: title.backCoverUrl, alt: title.backCoverAlt || `${title.title} back cover` }} title={`${title.title} back cover`} width={700} height={900} detail /></div>
 </div>
 </section> : null}
 </div> : null}
 </div>
 ) : (
 <Card className="rounded-[2rem] border border-blush/70 bg-white shadow-sm"><CardContent className="p-6 text-sm text-charcoal/75">This title is not published yet or does not exist.</CardContent></Card>
 )}
 </div>
 </section>
 );
}

export function PublishedTitlesShowcase({ heading = 'Published from Lumina Press' }: { heading?: string }) {
 const { titles, loading, error } = usePublicTitles();
 if (loading) return <p className="mt-8 text-sm text-charcoal/70">Loading published titles...</p>;
 if (error || !titles.length) return null;

 return (
 <div className="mt-16">
 <div className="mb-6 flex items-center justify-between gap-4">
 <div>
 <h2 className="font-headline text-3xl text-charcoal">{heading}</h2>
 <p className="mt-2 text-sm text-charcoal/70">Featured books from The Untangled Life and Lumina Press.</p>
 </div>
 <Button asChild variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60"><Link href={ROUTES.titles}>View all titles</Link></Button>
 </div>
 <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
 {titles.slice(0, 6).map((title) => <TitleCard key={title.titleId} title={title} />)}
 </div>
 </div>
 );
}
